import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { goalLibrarySectionsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Check, BookOpen, Loader2 } from 'lucide-react';

export default function SectionsPanel({ sections, selectedSection, onSelectSection, onSectionsChange, templateId }) {
  const [editingId, setEditingId] = useState(null); // null | 'new' | sectionId
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const startNew = () => {
    setEditingId('new');
    setEditName('');
  };

  const startEdit = (section) => {
    setEditingId(section.id);
    setEditName(section.name);
  };

  const cancel = () => {
    setEditingId(null);
    setEditName('');
  };

  const save = async () => {
    if (!editName.trim()) {
      toast.error('Section name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        await goalLibrarySectionsApi.create(templateId, { name: editName.trim() });
        toast.success('Section created');
      } else {
        await goalLibrarySectionsApi.update(editingId, { name: editName.trim() });
        toast.success('Section updated');
      }
      cancel();
      onSectionsChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (section) => {
    if (!window.confirm(`Delete section "${section.name}"? This will fail if the section still has goals — remove all goals first.`)) return;
    try {
      await goalLibrarySectionsApi.delete(section.id);
      toast.success('Section deleted');
      if (selectedSection?.id === section.id) onSelectSection(null);
      onSectionsChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete section');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') cancel();
  };

  return (
    <div className="flex flex-col h-full border-r bg-slate-50">
      {/* Panel header */}
      <div className="flex items-center justify-between px-3 py-3 border-b bg-white">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <BookOpen className="h-4 w-4 text-primary" />
          Sections
        </span>
        <Button variant="ghost" size="sm" onClick={startNew} className="h-7 w-7 p-0" title="Add section">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* New section input */}
      {editingId === 'new' && (
        <div className="px-2 py-2 border-b bg-white flex items-center gap-1">
          <Input
            autoFocus
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Section name"
            className="h-7 text-xs"
          />
          <Button variant="ghost" size="sm" onClick={save} disabled={saving} className="h-7 w-7 p-0 text-emerald-600">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={cancel} className="h-7 w-7 p-0 text-slate-500">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Section list */}
      <div className="flex-1 overflow-y-auto py-1">
        {sections.length === 0 && editingId !== 'new' && (
          <p className="text-xs text-muted-foreground text-center px-3 py-6">
            No sections yet. Click + to create one.
          </p>
        )}
        {sections.map((section) => (
          <div
            key={section.id}
            className={`group flex items-center gap-1 px-2 py-1.5 cursor-pointer rounded mx-1 my-0.5 transition-colors ${
              selectedSection?.id === section.id
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-slate-100 text-slate-700'
            }`}
          >
            {editingId === section.id ? (
              <div className="flex items-center gap-1 flex-1">
                <Input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="h-6 text-xs flex-1"
                  onClick={(e) => e.stopPropagation()}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); save(); }}
                  disabled={saving}
                  className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700"
                >
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); cancel(); }}
                  className="h-6 w-6 p-0 text-slate-500"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <>
                <span
                  className="flex-1 text-xs font-medium truncate"
                  onClick={() => onSelectSection(section)}
                >
                  {section.name}
                </span>
                {section.template_count != null && (
                  <Badge
                    variant="outline"
                    className={`text-xs h-4 px-1 ${selectedSection?.id === section.id ? 'border-primary-foreground/40 text-primary-foreground' : ''}`}
                  >
                    {section.template_count}
                  </Badge>
                )}
                <div className={`flex gap-0.5 ${selectedSection?.id === section.id ? 'opacity-80' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); startEdit(section); }}
                    className={`h-5 w-5 p-0 ${selectedSection?.id === section.id ? 'text-primary-foreground hover:bg-primary/80' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(section); }}
                    className={`h-5 w-5 p-0 ${selectedSection?.id === section.id ? 'text-primary-foreground hover:bg-primary/80' : 'text-red-500 hover:text-red-700'}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
